/**
 * Get currency symbol using Intl.NumberFormat for maximum compatibility
 * Falls back to manual mapping for currencies that need special handling
 */
export const getCurrencySymbol = (currency: string): string => {
    if (!currency || currency.trim() === '') {
        return '$';
    }
    
    // Special cases that need manual handling
    const specialSymbols: Record<string, string> = {
        'BDT': '৳',
        'USD': '$',
        'GBP': '£',
        'EUR': '€',
        'JPY': '¥',
        'INR': '₹',
        'CNY': '¥',
        'KRW': '₩',
        'RUB': '₽',
        'TRY': '₺',
        'ILS': '₪',
        'PHP': '₱',
        'THB': '฿',
    };
    
    // Return special symbol if available
    if (specialSymbols[currency]) {
        return specialSymbols[currency];
    }
    
    // Use Intl.NumberFormat to get symbol for any ISO currency code
    try {
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
        
        // Extract symbol from formatted string (e.g., "$1" -> "$")
        const parts = formatter.formatToParts(1);
        const symbolPart = parts.find(part => part.type === 'currency');
        
        if (symbolPart && symbolPart.value) {
            return symbolPart.value;
        }
    } catch (error) {
        // If Intl.NumberFormat fails, fall back to currency code
    }
    
    // Final fallback: return currency code
    return currency;
};

export const formatCurrency = (amount: number, currency: string = 'USD') => {
    if (!currency || currency.trim() === '') {
        currency = 'USD';
    }
    
    const symbol = getCurrencySymbol(currency);
    const isNegative = amount < 0;
    const absAmount = Math.abs(amount);
    
    if (currency === 'BDT') {
        const formatted = absAmount.toLocaleString('en-BD', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        });
        return isNegative ? `-${symbol}${formatted}` : `${symbol}${formatted}`;
    }
    
    try {
        const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            currencyDisplay: 'narrowSymbol'
        }).format(absAmount).replace(currency, symbol);
        return isNegative ? `-${formatted}` : formatted;
    } catch (error) {
        const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            currencyDisplay: 'narrowSymbol'
        }).format(absAmount).replace('USD', symbol);
        return isNegative ? `-${formatted}` : formatted;
    }
};

/** Helvetica-safe currency strings for jsPDF (falls back to "CODE amount" for non-ASCII symbols). */
export const formatCurrencyForPdf = (amount: number, currency: string = 'USD'): string => {
    const formatted = formatCurrency(amount, currency);
    // ASCII-only check (eslint no-control-regex: intentional range for printable ASCII)
    // eslint-disable-next-line no-control-regex
    if (!/[^\x00-\x7F]/.test(formatted)) return formatted;

    const code = currency?.trim() || 'USD';
    const isNegative = amount < 0;
    const absAmount = Math.abs(amount);
    const number =
        code === 'BDT'
            ? absAmount.toLocaleString('en-BD', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : absAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${isNegative ? '-' : ''}${code} ${number}`;
};

export const currencyCodeLabelForPdf = (currency: string): string => {
    const code = currency?.trim() || 'USD';
    const symbol = getCurrencySymbol(code);
    // eslint-disable-next-line no-control-regex
    return /^[\x00-\x7F]+$/.test(symbol) ? `${code} (${symbol})` : code;
};

/**
 * Format currency in compact, human-readable format for large numbers
 * Examples: ৳1.8M, ৳100.5K, ৳500.00
 * Abbreviates numbers >= 10K as K, >= 1M as M
 */
export const formatCurrencyCompact = (amount: number, currency: string = 'USD'): string => {
    if (!currency || currency.trim() === '') {
        currency = 'USD';
    }
    
    const symbol = getCurrencySymbol(currency);
    const absAmount = Math.abs(amount);
    
    // For numbers >= 1 million, show as M
    if (absAmount >= 1000000) {
        const millions = absAmount / 1000000;
        // Show 1 decimal place, but remove trailing zero
        const formatted = millions.toFixed(1).replace(/\.0$/, '');
        return `${symbol}${formatted}M`;
    }
    
    // For numbers >= 10K, show as K
    if (absAmount >= 10000) {
        const thousands = absAmount / 1000;
        // Show 1 decimal place, but remove trailing zero
        const formatted = thousands.toFixed(1).replace(/\.0$/, '');
        return `${symbol}${formatted}K`;
    }
    
    // For smaller numbers, use regular formatting
    if (currency === 'BDT') {
        return `${symbol}${absAmount.toLocaleString('en-BD', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        })}`;
    }
    
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            currencyDisplay: 'narrowSymbol'
        }).format(absAmount).replace(currency, symbol);
    } catch (error) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            currencyDisplay: 'narrowSymbol'
        }).format(absAmount).replace('USD', symbol);
    }
}; 

